import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const FacebookCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [accessToken, setAccessToken] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testMessage, setTestMessage] = useState('Hello from KnackVibes! 🚀 This is a test post.');
  const [testImageFile, setTestImageFile] = useState(null);
  const [testImageUrl, setTestImageUrl] = useState('');
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    // Log the full URL to see what we're receiving
    console.log('🔵 Current URL:', window.location.href);
    console.log('🔵 Search params:', Array.from(searchParams.entries()));
    
    // Get all query parameters from URL
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');
    
    // Extract all response data
    const responseData = {
      success: success === 'true',
      error: error,
      errorDescription: errorDescription,
      accessToken: searchParams.get('accessToken'),
      tokenId: searchParams.get('tokenId'),
      userId: searchParams.get('userId'),
      userName: searchParams.get('userName'),
      userEmail: searchParams.get('userEmail'),
      userProfilePicture: searchParams.get('userProfilePicture'),
      tokenExpiresIn: searchParams.get('tokenExpiresIn'),
      pagesCount: searchParams.get('pagesCount'),
      instagramCount: searchParams.get('instagramCount'),
      firstPageId: searchParams.get('firstPageId'),
      firstPageName: searchParams.get('firstPageName'),
      firstPagePicture: searchParams.get('firstPagePicture')
    };

    // Console log all the response data
    console.log('========================================');
    console.log('📥 Facebook OAuth Callback Response');
    console.log('========================================');
    console.log('Success:', responseData.success);
    console.log('Error:', responseData.error);
    console.log('Error Description:', responseData.errorDescription);
    
    if (responseData.success) {
      console.log('✅ Access Token:', responseData.accessToken);
      console.log('✅ Token ID:', responseData.tokenId);
      console.log('✅ User ID:', responseData.userId);
      console.log('✅ User Name:', responseData.userName);
      console.log('✅ User Email:', responseData.userEmail);
      console.log('✅ User Profile Picture:', responseData.userProfilePicture);
      console.log('✅ Token Expires In:', responseData.tokenExpiresIn, 'seconds');
      console.log('✅ Facebook Pages Count:', responseData.pagesCount);
      console.log('✅ Instagram Accounts Count:', responseData.instagramCount);
      console.log('✅ First Page ID:', responseData.firstPageId);
      console.log('✅ First Page Name:', responseData.firstPageName);
      console.log('✅ First Page Picture:', responseData.firstPagePicture);
      
      // Store token in localStorage and state for testing
      if (responseData.accessToken) {
        localStorage.setItem('facebookAccessToken', responseData.accessToken);
        localStorage.setItem('facebookTokenId', responseData.tokenId);
        setAccessToken(responseData.accessToken);
        console.log('💾 Token stored in localStorage for testing');
        
        // Add test functions to window for easy access
        window.testFacebookPost = async (message = 'Hello from KnackVibes! 🚀 Test post', imageUrl = null) => {
          const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
          try {
            console.log('📤 Testing Facebook post...');
            const body = {
              accessToken: responseData.accessToken,
              message: message
            };
            if (imageUrl) {
              body.imageUrl = imageUrl;
            }
            
            const response = await fetch(`${API_BASE_URL}/api/postToFacebook`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(body)
            });
            const result = await response.json();
            console.log('📥 Facebook Post Response:', result);
            return result;
          } catch (error) {
            console.error('❌ Error testing Facebook post:', error);
            return { error: error.message };
          }
        };
        
        // Test with image file
        window.testFacebookPostWithImage = async (message = 'Hello from KnackVibes! 🚀 Test post with image', imageFile) => {
          const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
          try {
            console.log('📤 Testing Facebook post with image file...');
            const formData = new FormData();
            formData.append('accessToken', responseData.accessToken);
            formData.append('message', message);
            if (imageFile) {
              formData.append('image', imageFile);
            }
            
            const response = await fetch(`${API_BASE_URL}/api/postToFacebook`, {
              method: 'POST',
              body: formData
            });
            const result = await response.json();
            console.log('📥 Facebook Post Response:', result);
            return result;
          } catch (error) {
            console.error('❌ Error testing Facebook post:', error);
            return { error: error.message };
          }
        };
        
        console.log('🧪 Test functions available!');
        console.log('  - testFacebookPost("Your message") - Text only');
        console.log('  - testFacebookPost("Your message", "https://image-url.com/image.jpg") - With image URL');
        console.log('  - testFacebookPostWithImage("Your message", fileObject) - With image file');
      }
    } else if (responseData.error) {
      console.error('❌ Error Code:', responseData.error);
      console.error('❌ Error Description:', responseData.errorDescription);
    } else {
      console.warn('⚠️ No success flag and no error - might be initial page load');
    }
    
    console.log('');
    console.log('📋 Full Response Object:');
    console.log(JSON.stringify(responseData, null, 2));
    console.log('========================================');

    // Don't auto-redirect if we have a token (allow testing)
    if (!responseData.success || !responseData.accessToken) {
      setTimeout(() => {
        navigate('/pagehome');
      }, 2000);
    }

  }, [searchParams, navigate]);

  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setTestResult({ error: 'Please select an image file' });
        return;
      }
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setTestResult({ error: 'Image file size must be less than 10MB' });
        return;
      }
      setTestImageFile(file);
      setTestImageUrl(''); // Clear URL if file is selected
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    setTestImageUrl(url);
    if (url) {
      setTestImageFile(null); // Clear file if URL is provided
      setImagePreview(url);
    } else {
      setImagePreview(null);
    }
  };

  const handleTestPost = async () => {
    if (!accessToken) {
      setTestResult({ error: 'No access token available' });
      return;
    }

    if (!testMessage || testMessage.trim() === '') {
      setTestResult({ error: 'Please enter a message' });
      return;
    }

    // Validate character limit (5000)
    if (testMessage.length > 5000) {
      setTestResult({ 
        error: `Message exceeds Facebook character limit (5000 characters). Your message has ${testMessage.length} characters.`,
        details: { maxLength: 5000, currentLength: testMessage.length }
      });
      return;
    }

    setTestResult({ loading: true });
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
    
    try {
      let response;
      
      if (testImageFile) {
        // Post with image file
        const formData = new FormData();
        formData.append('accessToken', accessToken);
        formData.append('message', testMessage);
        formData.append('image', testImageFile);
        
        response = await fetch(`${API_BASE_URL}/api/postToFacebook`, {
          method: 'POST',
          body: formData
        });
      } else if (testImageUrl && testImageUrl.trim() !== '') {
        // Post with image URL
        response = await fetch(`${API_BASE_URL}/api/postToFacebook`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            accessToken: accessToken,
            message: testMessage,
            imageUrl: testImageUrl.trim()
          })
        });
      } else {
        // Post text only
        response = await fetch(`${API_BASE_URL}/api/postToFacebook`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          accessToken: accessToken,
            message: testMessage
        })
      });
      }
      
      const result = await response.json();
      setTestResult(result);
      console.log('📥 Facebook Post Response:', result);
    } catch (error) {
      setTestResult({ error: error.message });
      console.error('❌ Error testing Facebook post:', error);
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      minHeight: '100vh',
      padding: '20px'
    }}>
      <div style={{ textAlign: 'center', maxWidth: '600px' }}>
        <h2>Facebook Login Successful! ✅</h2>
        
        {searchParams.get('userProfilePicture') && (
          <div style={{ marginBottom: '20px' }}>
            <img 
              src={searchParams.get('userProfilePicture')} 
              alt="Profile" 
              style={{
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                border: '3px solid #4267B2',
                objectFit: 'cover'
              }}
            />
            <p style={{ marginTop: '10px', fontWeight: 'bold' }}>
              {searchParams.get('userName')}
            </p>
          </div>
        )}
        
        {searchParams.get('firstPagePicture') && (
          <div style={{ marginBottom: '20px' }}>
            <p style={{ fontSize: '14px', color: '#666' }}>Connected Page:</p>
            <img 
              src={searchParams.get('firstPagePicture')} 
              alt={searchParams.get('firstPageName')} 
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '8px',
                border: '2px solid #4267B2',
                objectFit: 'cover'
              }}
            />
            <p style={{ marginTop: '10px', fontSize: '14px' }}>
              {searchParams.get('firstPageName')}
            </p>
          </div>
        )}
        
        <p>Check the browser console for full response details.</p>
        
        {searchParams.get('error') && (
          <div style={{ 
            marginTop: '20px', 
            padding: '15px', 
            backgroundColor: '#fee', 
            border: '1px solid #fcc',
            borderRadius: '5px',
            color: '#c33'
          }}>
            <strong>Error:</strong> {searchParams.get('error')}<br/>
            {searchParams.get('error_description') && (
              <span>{searchParams.get('error_description')}</span>
            )}
          </div>
        )}

        {accessToken && (
          <div style={{ marginTop: '30px', width: '100%', maxWidth: '500px' }}>
            {/* Message Input */}
            <div style={{ marginBottom: '15px', textAlign: 'left' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                Post Message:
              </label>
              <textarea
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                placeholder="Enter your post message..."
                rows={4}
                maxLength={5000}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
              <div style={{ fontSize: '12px', color: '#666', marginTop: '5px', textAlign: 'right' }}>
                {testMessage.length} / 5000 characters
              </div>
            </div>

            {/* Image Upload Options */}
            <div style={{ marginBottom: '15px', textAlign: 'left' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                Add Image (Optional):
              </label>
              
              {/* Option 1: Upload File */}
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', color: '#666' }}>
                  Upload Image File:
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageFileChange}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    fontSize: '12px'
                  }}
                />
              </div>

              {/* Option 2: Image URL */}
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', color: '#666' }}>
                  Or Enter Image URL:
                </label>
                <input
                  type="url"
                  value={testImageUrl}
                  onChange={handleImageUrlChange}
                  placeholder="https://example.com/image.jpg"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    fontSize: '12px'
                  }}
                />
              </div>

              {/* Image Preview */}
              {imagePreview && (
                <div style={{ marginTop: '10px', textAlign: 'center' }}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '200px',
                      borderRadius: '5px',
                      border: '1px solid #ddd'
                    }}
                  />
                  <button
                    onClick={() => {
                      setImagePreview(null);
                      setTestImageFile(null);
                      setTestImageUrl('');
                    }}
                    style={{
                      marginTop: '5px',
                      padding: '5px 10px',
                      fontSize: '11px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer'
                    }}
                  >
                    Remove Image
                  </button>
                </div>
              )}
            </div>

            {/* Post Button */}
            <button
              onClick={handleTestPost}
              disabled={testResult?.loading}
              style={{
                width: '100%',
                padding: '12px 24px',
                fontSize: '16px',
                backgroundColor: '#4267B2',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: testResult?.loading ? 'not-allowed' : 'pointer',
                opacity: testResult?.loading ? 0.6 : 1,
                fontWeight: 'bold'
              }}
            >
              {testResult?.loading ? 'Posting...' : '🧪 Post to Facebook'}
            </button>

            {testResult && !testResult.loading && (
              <div style={{
                marginTop: '20px',
                padding: '15px',
                backgroundColor: testResult.success ? '#efe' : '#fee',
                border: `1px solid ${testResult.success ? '#cfc' : '#fcc'}`,
                borderRadius: '5px',
                color: testResult.success ? '#3c3' : '#c33'
              }}>
                {testResult.success ? (
                  <div>
                    <strong>✅ Post Successful!</strong><br/>
                    {testResult.hasImage && <span>📸 Post includes image<br/></span>}
                    Post ID: {testResult.postId}<br/>
                    {testResult.postUrl && (
                      <a href={testResult.postUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#4267B2' }}>
                        View Post on Facebook
                      </a>
                    )}
                  </div>
                ) : (
                  <div>
                    <strong>❌ Post Failed:</strong><br/>
                    {testResult.error || testResult.message}
                    {testResult.details && (
                      <pre style={{ fontSize: '12px', marginTop: '10px', textAlign: 'left' }}>
                        {JSON.stringify(testResult.details, null, 2)}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            )}

            <p style={{ color: '#666', fontSize: '12px', marginTop: '20px' }}>
              Or test from console: <code>testFacebookPost("Your message")</code>
            </p>
          </div>
        )}

        {!accessToken && !searchParams.get('error') && (
          <p style={{ color: '#666', fontSize: '14px', marginTop: '20px' }}>
            Redirecting to homepage...
          </p>
        )}

        {accessToken && (
          <button
            onClick={() => navigate('/pagehome')}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              fontSize: '14px',
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Go to Homepage
          </button>
        )}
      </div>
    </div>
  );
};

export default FacebookCallback;

